import {
  CheckOutlined,
  LoadingOutlined,
  PlusCircleFilled,
  ReloadOutlined,
  SearchOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  message,
  Popover,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { Content } from "antd/lib/layout/layout";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import cuentaAuxItems from "../../api/cuentaAux.json";
import {
  baseurlwc,
  credentials,
  GetAllCategorys,
  GetAllTermsAttributes,
} from "../../api/api";
import { baseurl } from "../../api/api";
import { GetTokenProducto, TypeIva } from "../../api/utils";

const { Option } = Select;
const { Title } = Typography;

const ACCOUNTING_TYPES = [
  { name: "cat0", label: "Inventario", tipo: 0 },
  { name: "cat1", label: "Ventas", tipo: 1 },
  { name: "cat2", label: "Costo de Ventas", tipo: 2 },
  { name: "cat3", label: "Devolución Compras", tipo: 4 },
  { name: "cat4", label: "Devolución Ventas", tipo: 5 },
];

function fixEncoding(str = "") {
  return str.replace(/Ã\?/g, "Ñ");
}

const accountingRows = ACCOUNTING_TYPES.reduce((acc, item, index) => {
  if (index % 2 === 0) acc.push([]);
  acc[acc.length - 1].push(item);
  return acc;
}, []);

export const AgregarVariacion = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productAttributes, setProductAttributes] = useState([]);
  const [attributeTerms, setAttributeTerms] = useState({});
  const [categorys, setCategorys] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [reloadingTerms, setReloadingTerms] = useState(false);
  const [form] = Form.useForm();

  toast.configure();

  useEffect(() => {
    GetAllCategorys()
      .then((res) => setCategorys(res.data || []))
      .catch(() => toast.error("Error al cargar categorías"));
  }, []);

  const searchProducts = useCallback(async (search) => {
    if (!search || search.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setLoadingSearch(true);
    try {
      const res = await axios.get(
        `${baseurlwc}/products?search=${encodeURIComponent(search.trim())}&type=variable&per_page=20&${credentials}`
      );
      setSearchResults(res.data || []);
    } catch (error) {
      toast.error(
        "Error al buscar productos: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoadingSearch(false);
    }
  }, []);

  const selectProduct = async (productId) => {
    setLoadingAttributes(true);
    try {
      const res = await axios.get(
        `${baseurlwc}/products/${productId}?${credentials}`
      );
      const product = res.data;
      setSelectedProduct(product);

      const attrs = product.attributes || [];
      setProductAttributes(attrs);

      const termsMap = {};
      await Promise.all(
        attrs.map(async (attr) => {
          const termsRes = await GetAllTermsAttributes(attr.id);
          termsMap[attr.id] = termsRes.data || [];
        })
      );
      setAttributeTerms(termsMap);
    } catch (error) {
      toast.error(
        "Error al cargar el producto: " +
          (error.response?.data?.message || error.message)
      );
      setSelectedProduct(null);
    } finally {
      setLoadingAttributes(false);
    }
  };

  const changeProduct = () => {
    setSelectedProduct(null);
    setProductAttributes([]);
    setAttributeTerms({});
    setSearchResults([]);
    form.resetFields();
  };

  const reloadTerms = async () => {
    if (!selectedProduct) return;
    setReloadingTerms(true);
    try {
      const attrs = selectedProduct.attributes || [];
      const termsMap = {};
      await Promise.all(
        attrs.map(async (attr) => {
          const termsRes = await GetAllTermsAttributes(attr.id);
          termsMap[attr.id] = termsRes.data || [];
        })
      );
      setAttributeTerms(termsMap);
      toast.success("Términos actualizados correctamente");
    } catch (error) {
      toast.error(
        "Error al actualizar términos: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setReloadingTerms(false);
    }
  };

  const onFinish = (values) => {
    if (!selectedProduct) return;

    const descTerms = productAttributes
      .map((attr) => values[`term-${attr.id}`])
      .filter(Boolean)
      .join("-");

    const productData = {
      TipoProducto: 1,
      IdIva: 0,
      Codigo: values.Codigo || "",
      Descripcion: `${selectedProduct.name}-${descTerms}`,
      IdCategoria: values.IdCategoria,
      IdSubCategoria: values.IdSubCategoria,
      Categoria: categorys.find((c) => c.id === values.IdCategoria)?.name || "",
      SubCategoria:
        categorys.find((c) => c.id === values.IdSubCategoria)?.name || "",
      PrecioVentaConIva1: values.PrecioVentaConIva1,
      PrecioVentaConIva2: values.PrecioVentaConIva1,
      PrecioVentaConIva3: values.PrecioVentaConIva1,
      PorcentajeIva: values.PorcentajeIva,
      weight: values.weight,
      dimensions: {
        length: values.length,
        width: values.width,
        height: values.height,
      },
      ParametrizacionContableProducto: ACCOUNTING_TYPES.map((t) => ({
        NumeroCuenta: values[t.name] || "",
        Tipo: t.tipo,
      })),
    };

    setLoadingSubmit(true);

    const getToken = GetTokenProducto(productData);
    toast.promise(getToken, {
      pending: "Obteniendo token de ARI...",
      success: "Token obtenido",
      error: "Error al obtener token de ARI",
    });

    getToken
      .then((tokenRes) => {
        if (!tokenRes?.data) {
          toast.error("No se pudo obtener el token de ARI");
          setLoadingSubmit(false);
          return Promise.reject(new Error("__no_token__"));
        }

        const productToSave = { ...productData, Token: tokenRes.data };

        toast.info("Guardando variación en ARI...", {
          position: "top-right",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        return axios.post(
          `${baseurl}GuardarProductoConContabilidad`,
          productToSave
        );
      })
      .then((ariRes) => {
        if (!ariRes) return Promise.reject(new Error("__no_ari__"));

        if (!ariRes.data.Exito) {
          toast.error("Error en ARI: " + ariRes.data.MensajeError);
          setLoadingSubmit(false);
          return Promise.reject(new Error("__ari_error__"));
        }

        toast.success("Variación guardada en ARI correctamente");

        // Actualizar atributos del producto padre para incluir el nuevo término
        const updatedAttributes = productAttributes.map((attr) => {
          const termName = values[`term-${attr.id}`];
          const currentOptions = attr.options || [];
          const newOptions = currentOptions.includes(termName)
            ? currentOptions
            : [...currentOptions, termName];
          return {
            id: attr.id,
            options: newOptions,
            visible: true,
            variation: true,
          };
        });

        const filteredAttributes = productAttributes
          .map((attr) => {
            const termName = values[`term-${attr.id}`];
            if (!termName) return null;
            const terms = attributeTerms[attr.id] || [];
            const termObj = terms.find((t) => t.name === termName);
            return {
              id: attr.id,
              option: termName,
              name: attr.name,
              slug:
                termObj?.slug || termName.toLowerCase().replace(/\s+/g, "-"),
            };
          })
          .filter(Boolean);

        const variationWC = {
          regular_price: `${values.PrecioVentaConIva1}`,
          price: values.PrecioVentaConIva1,
          manage_stock: true,
          weight: values.weight,
          dimensions: {
            length: values.length,
            width: values.width,
            height: values.height,
          },
          sku: ariRes.data.CodigoBarras,
          attributes: filteredAttributes,
          meta_data: [{ key: "referencia", value: values.Codigo || "" }],
        };

        toast.info("Actualizando atributos del producto padre...", {
          position: "top-right",
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Primero actualizar atributos del padre (secuencial, no paralelo)
        // para que el término quede registrado antes de crear la variación
        return axios
          .put(
            `${baseurlwc}/products/${selectedProduct.id}?${credentials}`,
            { attributes: updatedAttributes }
          )
          .then(() => {
            toast.info("Creando variación en WooCommerce...", {
              position: "top-right",
              autoClose: 10000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
            return axios.post(
              `${baseurlwc}/products/${selectedProduct.id}/variations?${credentials}`,
              variationWC
            );
          });
      })
      .then((wcVariationRes) => {
        if (!wcVariationRes) return;
        if (wcVariationRes.data.id) {
          toast.success("¡Variación creada correctamente en WooCommerce!", {
            position: "top-right",
            autoClose: 5000,
          });
        } else {
          toast.error(
            "Error al crear variación en WooCommerce: " +
              (wcVariationRes.data.message || "Error desconocido")
          );
        }
        setLoadingSubmit(false);
      })
      .catch((error) => {
        const internal = ["__no_token__", "__no_ari__", "__ari_error__"];
        if (!internal.includes(error.message)) {
          toast.error(
            "Error: " +
              (error.response?.data?.message || error.message)
          );
        }
        setLoadingSubmit(false);
      });
  };

  const handleFinalize = () => {
    setSelectedProduct(null);
    setSearchResults([]);
    setProductAttributes([]);
    setAttributeTerms({});
    setLoadingSubmit(false);
    form.resetFields();
  };

  return (
    <Content
      style={{
        marginTop: "20px",
        overflow: "initial",
        padding: "0 16px",
        marginLeft: "135px",
      }}
    >
      <Row justify="center">
        <Card
          style={{
            borderRadius: "16px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
            width: "100%",
            maxWidth: "70vw",
            margin: "0 auto",
            padding: "24px",
          }}
          title="Agregar Variación a Producto Existente"
        >
          {/* BUSCAR PRODUCTO */}
          {!selectedProduct && !loadingAttributes && (
            <div style={{ marginBottom: "32px" }}>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: 500,
                  marginBottom: "16px",
                  color: "#555",
                }}
              >
                Buscar Producto Variable
              </h3>
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={18}>
                  <Select
                    showSearch
                    style={{ width: "100%" }}
                    placeholder="Escribe el nombre del producto para buscar..."
                    filterOption={false}
                    onSearch={searchProducts}
                    onChange={selectProduct}
                    loading={loadingSearch}
                    notFoundContent={
                      loadingSearch ? (
                        <Spin size="small" />
                      ) : (
                        "Escribe al menos 2 caracteres para buscar"
                      )
                    }
                    suffixIcon={
                      loadingSearch ? (
                        <LoadingOutlined />
                      ) : (
                        <SearchOutlined />
                      )
                    }
                  >
                    {searchResults.map((product) => (
                      <Option key={product.id} value={product.id}>
                        <Space>
                          <ShoppingOutlined />
                          {product.name}
                          <Tag color="blue" style={{ marginLeft: 4 }}>
                            ID: {product.id}
                          </Tag>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            </div>
          )}

          {/* CARGANDO ATRIBUTOS */}
          {loadingAttributes && (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <LoadingOutlined style={{ fontSize: "32px", color: "#1890ff" }} />
              <p style={{ marginTop: "16px", color: "#555" }}>
                Cargando datos del producto...
              </p>
            </div>
          )}

          {/* FORMULARIO */}
          {selectedProduct && !loadingAttributes && (
            <>
              {/* Cabecera del producto seleccionado */}
              <div
                style={{
                  marginBottom: "24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <Space>
                  <ShoppingOutlined
                    style={{ fontSize: "20px", color: "#1890ff" }}
                  />
                  <Title level={5} style={{ margin: 0 }}>
                    {selectedProduct.name}
                  </Title>
                  <Tag color="blue">ID: {selectedProduct.id}</Tag>
                </Space>
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    loading={reloadingTerms}
                    onClick={reloadTerms}
                    size="small"
                    style={{ borderRadius: "8px" }}
                  >
                    Actualizar términos
                  </Button>
                  <Button danger size="small" onClick={changeProduct}>
                    Cambiar producto
                  </Button>
                </Space>
              </div>
              <Divider />

              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                onFinishFailed={() =>
                  message.info("Por favor complete todos los campos requeridos")
                }
                autoComplete="off"
                initialValues={{
                  cat0: "143506",
                  cat1: "413506",
                  cat2: "613506",
                  cat3: "622505",
                  cat4: "417506",
                  IdCategoria: selectedProduct?.categories?.[0]?.id,
                  IdSubCategoria: selectedProduct?.categories?.[1]?.id,
                }}
              >
                {/* CÓDIGO DE REFERENCIA */}
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                      marginBottom: "16px",
                      color: "#555",
                    }}
                  >
                    Información Básica
                  </h3>
                  <Row
                    gutter={[24, 16]}
                    style={{ marginLeft: "-12px", marginRight: "-12px" }}
                  >
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Código de Referencia"
                        name="Codigo"
                        tooltip="Código único para identificar esta variante (opcional)"
                      >
                        <Input
                          placeholder="Código de referencia único por variante"
                          maxLength={45}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="IdCategoria"
                        label="Categoría"
                        rules={[
                          {
                            required: true,
                            message: "Selecciona una categoría",
                          },
                        ]}
                        tooltip="Categoría que se enviará a ARI"
                      >
                        <Select
                          placeholder="Selecciona una categoría"
                          allowClear
                          showSearch
                          style={{ width: "100%" }}
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          filterSort={(a, b) =>
                            a.children
                              .toLowerCase()
                              .localeCompare(b.children.toLowerCase())
                          }
                        >
                          {categorys.map((cat) => (
                            <Option key={cat.id} value={cat.id}>
                              {cat.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="IdSubCategoria"
                        label="Subcategoría"
                        rules={[
                          {
                            required: true,
                            message: "Selecciona una subcategoría",
                          },
                        ]}
                        tooltip="Subcategoría que se enviará a ARI"
                      >
                        <Select
                          placeholder="Selecciona una subcategoría"
                          allowClear
                          showSearch
                          style={{ width: "100%" }}
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          filterSort={(a, b) =>
                            a.children
                              .toLowerCase()
                              .localeCompare(b.children.toLowerCase())
                          }
                        >
                          {categorys.map((cat) => (
                            <Option key={cat.id} value={cat.id}>
                              {cat.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                {/* SELECCIÓN DE TÉRMINOS */}
                {productAttributes.length > 0 && (
                  <div style={{ marginBottom: "32px" }}>
                    <h3
                      style={{
                        fontSize: "16px",
                        fontWeight: 500,
                        marginBottom: "16px",
                        color: "#555",
                      }}
                    >
                      Términos de la Variación
                    </h3>
                    <Row
                      gutter={[24, 16]}
                      style={{ marginLeft: "-12px", marginRight: "-12px" }}
                    >
                      {productAttributes.map((attr) => (
                        <Col xs={24} lg={12} key={attr.id}>
                          <Form.Item
                            name={`term-${attr.id}`}
                            label={attr.name}
                            rules={[
                              {
                                required: true,
                                message: `Selecciona un término para ${attr.name}`,
                              },
                            ]}
                            tooltip={`Selecciona el término de ${attr.name} para esta variación`}
                          >
                            <Select
                              placeholder={`Selecciona ${attr.name}`}
                              showSearch
                              allowClear
                              style={{ width: "100%" }}
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                option.children
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              }
                              filterSort={(a, b) =>
                                a.children
                                  .toLowerCase()
                                  .localeCompare(b.children.toLowerCase())
                              }
                            >
                              {(attributeTerms[attr.id] || []).map((term) => (
                                <Option key={term.id} value={term.name}>
                                  {term.name}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {/* PRECIOS Y PESO */}
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                      marginBottom: "16px",
                      color: "#555",
                    }}
                  >
                    Precios y Peso
                  </h3>
                  <Row
                    gutter={[24, 16]}
                    style={{ marginLeft: "-12px", marginRight: "-12px" }}
                  >
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Precio de venta"
                        name="PrecioVentaConIva1"
                        rules={[
                          {
                            required: true,
                            message: "Digite el precio de venta",
                          },
                        ]}
                        tooltip="Precio de venta al público (incluye IVA)"
                      >
                        <Input placeholder="$200.000" type="number" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Peso del producto"
                        name="weight"
                        rules={[
                          {
                            required: true,
                            message: "Digite el peso del producto",
                          },
                        ]}
                        tooltip="Peso del producto (ej: 1 kg, 0.3 kg)"
                      >
                        <Input
                          placeholder="(1): kg | (0.3): otros"
                          type="text"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Largo (cm)"
                        name="length"
                        rules={[{ required: true, message: "Digite el largo" }]}
                        tooltip="Largo del producto en centímetros"
                      >
                        <Input placeholder="Ej: 10" type="number" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Ancho (cm)"
                        name="width"
                        rules={[{ required: true, message: "Digite el ancho" }]}
                        tooltip="Ancho del producto en centímetros"
                      >
                        <Input placeholder="Ej: 5" type="number" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Alto (cm)"
                        name="height"
                        rules={[{ required: true, message: "Digite el alto" }]}
                        tooltip="Alto del producto en centímetros"
                      >
                        <Input placeholder="Ej: 2" type="number" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                {/* IVA */}
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                      marginBottom: "16px",
                      color: "#555",
                    }}
                  >
                    Impuestos
                  </h3>
                  <Row
                    gutter={[24, 16]}
                    style={{ marginLeft: "-12px", marginRight: "-12px" }}
                  >
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="PorcentajeIva"
                        label="Porcentaje IVA"
                        rules={[
                          {
                            required: true,
                            message: "Selecciona un porcentaje IVA",
                          },
                        ]}
                        tooltip="Selecciona el porcentaje de IVA aplicable"
                      >
                        <Select
                          placeholder="Selecciona un porcentaje IVA"
                          allowClear
                          style={{ width: "100%" }}
                        >
                          {TypeIva.map((typeiva) => (
                            <Option
                              key={typeiva.Codigo}
                              value={typeiva.Valor}
                            >
                              {typeiva.Descripcion}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                {/* PARAMETRIZACIÓN CONTABLE */}
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                      marginBottom: "16px",
                      color: "#555",
                    }}
                  >
                    Detalles Contables
                  </h3>
                  {accountingRows.map((row, rowIndex) => (
                    <Row
                      key={rowIndex}
                      gutter={[24, 16]}
                      style={{ marginLeft: "-12px", marginRight: "-12px" }}
                    >
                      {row.map((item, colIndex) => {
                        const labelText = `${item.tipo} - Parametrización contable para ${item.label}`;
                        return (
                          <Col key={colIndex} xs={24} lg={12}>
                            <Form.Item
                              name={item.name}
                              label={
                                <Popover content={labelText} placement="top">
                                  <span>
                                    {labelText.length > 45
                                      ? `${labelText.substring(0, 45)}...`
                                      : labelText}
                                  </span>
                                </Popover>
                              }
                              rules={[
                                {
                                  required: true,
                                  message: "Selecciona una categoría contable",
                                },
                              ]}
                              tooltip={`Cuenta contable para ${item.label}.`}
                            >
                              <Select
                                placeholder="Selecciona una categoría contable"
                                allowClear
                                showSearch
                                style={{ width: "100%" }}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  option.children
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                                }
                                filterSort={(a, b) =>
                                  a.children
                                    .toLowerCase()
                                    .localeCompare(b.children.toLowerCase())
                                }
                              >
                                {cuentaAuxItems.map((cat) => (
                                  <Option
                                    key={cat.SubCuentAuxiliarNumero}
                                    value={cat.SubCuentAuxiliarNumero}
                                  >
                                    {`${cat.SubCuentAuxiliarNumero} - ${fixEncoding(
                                      cat.SubCuentaAuxiliarNombre
                                    )}`}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                        );
                      })}
                    </Row>
                  ))}
                </div>

                {/* BOTONES */}
                <Form.Item style={{ textAlign: "center", marginTop: "24px" }}>
                  <Space size="large">
                    <Button
                      type="primary"
                      htmlType="submit"
                      shape="round"
                      size="large"
                      icon={
                        loadingSubmit ? (
                          <LoadingOutlined />
                        ) : (
                          <PlusCircleFilled />
                        )
                      }
                      disabled={loadingSubmit}
                      style={{
                        padding: "0 32px",
                        height: "48px",
                        fontSize: "16px",
                        fontWeight: 500,
                        borderRadius: "10px",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {loadingSubmit ? "Creando variación..." : "Crear variación"}
                    </Button>
                    <Button
                      shape="round"
                      size="large"
                      icon={<CheckOutlined />}
                      onClick={handleFinalize}
                      style={{
                        padding: "0 32px",
                        height: "48px",
                        fontSize: "16px",
                        fontWeight: 500,
                        borderRadius: "10px",
                        background: "linear-gradient(90deg, #52c41a, #73d13d)",
                        border: "none",
                        color: "#fff",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                        e.currentTarget.style.boxShadow =
                          "0 6px 16px rgba(82, 196, 26, 0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      Finalizar
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </>
          )}
        </Card>
      </Row>
    </Content>
  );
};

export default AgregarVariacion;